import {
  CellClassParams,
  ColSpanParams,
  GridOptions,
  ICellRendererFunc,
  IsColumnFunc,
  RowNode,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import React, { ComponentType, FC, useCallback, useState } from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';

type ComparisonResult = 0 | -1 | 1;
export type Comparator<T> = (
  valueA: T,
  valueB: T,
  recordA?: Record<string, unknown>,
  recordB?: Record<string, unknown>,
  isInverted?: boolean,
) => ComparisonResult;

export const toRowNodeComparator = <T,>(comparator?: Comparator<T>) =>
  comparator == null
    ? undefined
    : (
        valueA: T,
        valueB: T,
        nodeA: RowNode,
        nodeB: RowNode,
        isInverted: boolean,
      ) => comparator(valueA, valueB, nodeA.data, nodeB.data, isInverted);

export interface Column<T = any> {
  name: string;
  headerName?: string;
  editable: boolean | IsColumnFunc;
  valueGetter?: (params: ValueGetterParams) => T;
  valueFormatter?: (params: ValueFormatterParams) => string;
  renderer?: string | ICellRendererFunc;
  rendererParams?: any;
  rendererFramework?: any;
  editor?: any;
  editorParams?: any;
  comparator?: Comparator<T>;
  suppressKeyboardEvent?: (params: SuppressKeyboardEventParams) => boolean;
  headerTemplate?: string;
  initialWidth?: number;
  lockPinned?: boolean;
  lockPosition?: boolean;
  pinned?: boolean;
  group?: boolean;
  colSpan?: (params: ColSpanParams) => number;
  suppressMovable?: boolean;
  hide?: boolean;
  cellStyle?: any;
}

type KnownProps = Pick<GridOptions, 'columnDefs'>;

export type ColumnsProps = {
  columns: Column[];
};

export const withColumns = <T extends KnownProps>(
  Component: ComponentType<T>,
): FC<Omit<T, keyof KnownProps> & ColumnsProps> => {
  const Next: FC<Omit<T, keyof KnownProps> & ColumnsProps> = ({
    columns,
    ...props
  }) => {
    const getColumnDefs = useCallback(
      () =>
        columns.map(
          ({
            name,
            editable,
            renderer,
            rendererParams,
            rendererFramework,
            editor,
            editorParams,
            headerTemplate,
            pinned,
            group,
            comparator,
            ...column
          }) => ({
            ...column,
            showRowGroup: group,
            pinned: pinned ? 'left' : undefined,
            field: name,
            cellRenderer: renderer,
            cellRendererFramework: rendererFramework,
            cellRendererParams: rendererParams,
            cellEditorFramework: editor,
            cellEditorParams: editorParams,
            editable,
            cellClass:
              typeof editable === 'boolean'
                ? editable
                  ? ['grid-cell-editable']
                  : []
                : (params: CellClassParams) =>
                    editable({ ...params, column: null as any })
                      ? ['grid-cell-editable']
                      : [],
            headerComponentParams: {
              template: headerTemplate,
            },
            comparator: toRowNodeComparator(comparator),
          }),
        ),
      [columns],
    );

    // updating column def directly would break ag grid. use setColumnDefs instead
    const [columnDef] = useState(getColumnDefs());

    return <Component {...(props as any)} columnDefs={columnDef} />;
  };

  return setDisplayName(wrapDisplayName(Component, 'WithColumns'))(Next);
};
